import { yupResolver } from "@hookform/resolvers/yup";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useSettlementItems } from "~/hooks/useSettlementItems";
import type { NskResponse } from "~/models/Api";
import type {
	DealSettlement,
	DealSettlementEdit
} from "~/models/DealSettlement";
import { validationSchemaSettlement } from "~/models/DealSettlement";
import { isSuccess } from "~/utils/api/api_handler";
import { SettlementForm } from "../SettlementForm";
import { SettlementFormFooter } from "../SettlementFormFooter";

type Props = {
	dealSettlement: DealSettlement;
	handleClose: () => void;
	handleMutateSettlements: () => Promise<void>;
	handleUpdate: (
		submitValue: DealSettlementEdit,
		estimationId: number
	) => Promise<NskResponse>;
};

export const SettlementEditModal: React.FC<Props> = ({
	dealSettlement,
	handleMutateSettlements,
	handleClose,
	handleUpdate
}: Props) => {
	const { data: settlementItems } = useSettlementItems().index();
	const methods = useForm<DealSettlementEdit>({
		defaultValues: dealSettlement,
		mode: "onBlur",
		resolver: yupResolver(validationSchemaSettlement)
	});
	const onClickSubmit = React.useCallback(() => {
		methods.handleSubmit(async () => {
			const response = await handleUpdate(
				methods.getValues(),
				dealSettlement.id
			);
			if (isSuccess(response)) {
				await handleMutateSettlements();
				methods.reset();
				handleClose();
				toast.success("更新しました");
				return;
			}
			toast.error("エラーが発生しました");
			handleClose();
			methods.reset();
		})();
	}, [
		dealSettlement.id,
		handleClose,
		handleMutateSettlements,
		handleUpdate,
		methods
	]);
	if (settlementItems === null) return null;
	return (
		<Modalv2 isOpen onClose={handleClose}>
			<ModalHeader title="精算項目の修正" />
			<ModalBody>
				<FormProvider {...methods}>
					<FormGroup variant="vertical">
						<SettlementForm settlementItems={settlementItems} />
					</FormGroup>
					<SettlementFormFooter onClickSubmit={onClickSubmit} type="edit" />
				</FormProvider>
			</ModalBody>
		</Modalv2>
	);
};
